import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, imageUrl } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Plus, Trash2 } from "lucide-react";

function PdfSlot({ title, description, pathKey, nameKey, assets, refresh }) {
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(assets[nameKey] || "");
  const { current } = useChampionship() || {};

  useEffect(() => { setName(assets[nameKey] || ""); }, [assets, nameKey]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newName = name?.trim() || file.name;
      await api.patch(`/championships/${current.id}/site-assets`, {
        [pathKey]: data.storage_path,
        [nameKey]: newName,
      });
      toast.success("PDF uploaded");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const renameOnly = async () => {
    try {
      await api.patch(`/championships/${current.id}/site-assets`, { [nameKey]: name });
      toast.success("Saved");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!confirm("Remove this PDF link? The file remains in storage.")) return;
    try {
      await api.patch(`/championships/${current.id}/site-assets`, { [pathKey]: "", [nameKey]: "" });
      toast.success("Removed");
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const hasFile = !!assets[pathKey];

  let uploadLabel;
  if (uploading) uploadLabel = "Uploading…";
  else if (hasFile) uploadLabel = "Replace PDF";
  else uploadLabel = "Upload PDF";

  return (
    <div className="border border-border bg-white p-6" data-testid={`pdf-slot-${pathKey}`}>
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 bg-secondary border border-border flex items-center justify-center flex-shrink-0">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>

          {hasFile && (
            <div className="mt-4 inline-flex items-center gap-2 text-sm bg-secondary px-3 py-2">
              <FileText className="h-4 w-4" />
              <a href={imageUrl(assets[pathKey])} target="_blank" rel="noreferrer" className="text-primary underline">
                {assets[nameKey] || "Download current file"}
              </a>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </div>
          )}

          <div className="mt-5 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={hasFile ? "Edit display name" : "Set after upload"}
                data-testid={`pdf-name-${pathKey}`}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 text-sm font-medium rounded-md transition" data-testid={`pdf-upload-${pathKey}`}>
                <Upload className="h-4 w-4" />
                {uploadLabel}
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
              </label>
              {hasFile && (
                <>
                  <Button variant="outline" size="sm" onClick={renameOnly}>Save name</Button>
                  <Button variant="ghost" size="sm" onClick={remove} className="text-destructive">Remove</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSiteAssetsPage() {
  const { current, reload: reloadGlobal } = useChampionship() || {};
  const [assets, setAssets] = useState({});
  const [documents, setDocuments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newDocFile, setNewDocFile] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const [a, d] = await Promise.all([
      api.get(`/championships/${current.id}/site-assets`),
      api.get(`/championships/${current.id}/documents`),
    ]);
    setAssets(a.data || {});
    setDocuments(d.data || []);
  }, [current?.id]);

  useEffect(() => { load(); }, [load]);

  const addDocument = async (e) => {
    e?.preventDefault?.();
    if (!newDocName.trim() || !newDocFile) {
      toast.error("Please enter a name and pick a file");
      return;
    }
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("file", newDocFile);
      const { data: uploaded } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/championships/${current.id}/documents`, {
        name: newDocName.trim(),
        path: uploaded.storage_path,
        order: documents.length,
      });
      toast.success("Document added");
      setNewDocName("");
      setNewDocFile(null);
      setShowAdd(false);
      await load();
      reloadGlobal?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const renameDoc = async (doc, name) => {
    if (!name?.trim() || name === doc.name) return;
    try {
      await api.patch(`/documents/${doc.id}`, { name: name.trim() });
      toast.success("Renamed");
      await load();
      reloadGlobal?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeDoc = async (doc) => {
    if (!confirm(`Remove "${doc.name}" from the Information menu?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Removed");
      await load();
      reloadGlobal?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="admin-site-assets">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Documents</div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2 mb-2">Registration documents</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Both files appear at the top of the captain registration page for download.
      </p>

      <div className="space-y-4 max-w-3xl">
        <PdfSlot
          title="Registration details PDF"
          description="Federation registration instructions, fees, deadlines, etc."
          pathKey="registration_pdf_path"
          nameKey="registration_pdf_name"
          assets={assets}
          refresh={load}
        />
        <PdfSlot
          title="WOC tournament rules PDF"
          description="Official tournament rules and regulations document."
          pathKey="rules_pdf_path"
          nameKey="rules_pdf_name"
          assets={assets}
          refresh={load}
        />
      </div>

      <div className="mt-14 max-w-3xl">
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Information menu documents</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2">Additional documents</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Each document added here shows up under the public site's <strong>Information</strong> menu — just like the Tournament Rules link.
            </p>
          </div>
          {!showAdd && (
            <Button
              onClick={() => setShowAdd(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="add-document-button"
            >
              <Plus className="h-4 w-4 mr-2" /> Add another document
            </Button>
          )}
        </div>

        {showAdd && (
          <form onSubmit={addDocument} className="border border-border bg-white p-5 mt-4 space-y-4" data-testid="add-document-form">
            <div className="space-y-2">
              <Label>Name (shown in the Information menu)</Label>
              <Input
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="e.g. Player Code of Conduct"
                required
                data-testid="new-doc-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Upload Document</Label>
              <label className="inline-flex items-center gap-2 cursor-pointer bg-secondary hover:bg-secondary/70 px-4 py-2 text-sm font-medium rounded-md transition border border-border" data-testid="new-doc-upload-trigger">
                <Upload className="h-4 w-4" />
                {newDocFile ? newDocFile.name : "Choose file"}
                <input
                  type="file"
                  accept="application/pdf,.pdf,image/*"
                  className="hidden"
                  onChange={(e) => setNewDocFile(e.target.files?.[0] || null)}
                  data-testid="new-doc-file"
                />
              </label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={adding} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-doc-save">
                {adding ? "Uploading…" : "Add to Information menu"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setShowAdd(false); setNewDocName(""); setNewDocFile(null); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        <div className="mt-4 border border-border bg-white">
          {documents.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No additional documents yet.</div>
          ) : (
            documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-4 p-4 border-b border-border last:border-b-0" data-testid={`doc-row-${doc.id}`}>
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <input
                  defaultValue={doc.name}
                  onBlur={(e) => renameDoc(doc, e.target.value)}
                  className="flex-1 bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary outline-none px-1 py-1 text-sm font-medium"
                  data-testid={`doc-name-input-${doc.id}`}
                />
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline"
                >
                  Open ↗
                </a>
                <Button size="sm" variant="ghost" onClick={() => removeDoc(doc)} className="text-destructive" data-testid={`doc-delete-${doc.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
